import React, { useState, useRef,useEffect } from "react";
import { Form, Button, Row, Col, InputGroup, Modal, Stack } from "react-bootstrap";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    useDroppable // Add this import for useDroppable
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CKEditor } from '@ckeditor/ckeditor5-react';

//import Editor from "ckeditor5-custom-inline/build/ckeditor";
import EditorBuild from "ckeditor5-custom-classic/build/ckeditor";

import "./FormBuilder.css"; // Add this for custom styles

// Sortable item component for dnd-kit
function SortableField({ id, children, disableDrag }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: disableDrag ? "default" : "grab",
        opacity: isDragging ? 0.5 : 1,
        minWidth: "300px"
    };

    // Only spread listeners/attributes if drag is enabled
    return (
        <div
            ref={setNodeRef}
            style={style}
            {...(!disableDrag ? { ...attributes, ...listeners } : {})}
        >
            {children}
        </div>
    );
}

// Utility: Reorder schema so globalIdx is sequential by section order
function reorderSchemaBySections(schema, sections) {
    // Group fields by section, preserving their order
    const sectioned = [];
    sections.forEach(section => {
        sectioned.push(...schema.filter(f => f.section === section));
    });
    // Add fields not in any section at the end
    sectioned.push(...schema.filter(f => !f.section || !sections.includes(f.section)));
    return sectioned;
}

/**
 * Form Designer:
 * - Users can add, edit, and delete fields.
 * - Users can edit options for select fields.
 * - Users can reorder fields by drag-and-drop.
 * - Fields are numbered.
 * - Fields can be grouped into sections.
 */
export default function FormBuilder({
    formState,
    onChange = () => { },
    onSubmit = () => { },
}) {
    /*
    : {
        schema: initialSchema = [],
        values: initialValues = {},
        heading: initialHeading = "Form Title",
        subheading: initialSubheading = "",
        sections: initialSections = [],
        sectionsubtitles: initialSectionsubtitles = [],
    } = {}
    */

    // Replace multiple related pieces of state with a single combined state object
    const [form, setForm] = useState(() => ({
        heading: (formState && formState.heading) || "",
        subtitle: (formState && formState.subtitle) || "",
        schema: (formState && formState.schema) || [],
        values: (formState && formState.values) || {},
        sections: (formState && formState.sections) || [],
        sectionsubtitles: (formState && formState.sectionsubtitles) || [],
    }));

    // Replace separate editing state for header
    const [editingFormHeader, setEditingFormHeader] = useState(false);

    const [newField, setNewField] = useState({
        name: "",
        label: "",
        subtitle: "", // <-- Add subtitle to newField
        type: "text",
        required: false,
        section: "",
    });

    const handleFormHeadingChange = (value) => {
        setForm(prev => ({ ...prev, heading: value }));
    }
    const handleFormSubtitleChange = (value) => {
        setForm(prev => ({ ...prev, subtitle: value === "<p><br></p>" ? "" : value }));
    }

    // Section state
    const [newSection, setNewSection] = useState("");
    const [editingSectionIdx, setEditingSectionIdx] = useState(null);
    const [editingSectionValue, setEditingSectionValue] = useState("");
    const [editingSectionSubtitle, setEditingSectionSubtitle] = useState(""); // <-- Add this

    // Add Field Modal state for section-specific add
    const [showAddFieldModal, setShowAddFieldModal] = useState(false);
    const [addFieldSection, setAddFieldSection] = useState("");

    // Edit modal state
    const [editIndex, setEditIndex] = useState(null);
    const [editField, setEditField] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);

    // Option editing state for select fields
    const [newOption, setNewOption] = useState({ value: "", label: "" });

    // Selected question for inline editing
    const [selectedIdx, setSelectedIdx] = useState(null);

    // Notify parent any time the combined form state changes
    useEffect(() => {
        console.log(formState);
        onChange({
            heading: form.heading,
            subtitle: form.subtitle,
            schema: form.schema,
            sections: form.sections,
            sectionsubtitles: form.sectionsubtitles,
            values: form.values
        });
    }, [form]);

    // Add field with defaults
    const addDefaultField = (sectionName = "") => {
        const defaultField = {
            name: `field${form.schema.length + 1}`,
            label: `Field ${form.schema.length + 1}`,
            type: "text",
            required: false,
            section: form.sections.length > 0 ? (sectionName === "" ? form.sections[form.sections.length - 1] : sectionName) : "",
        };
        const newSchema = [...form.schema, defaultField];
        setForm(prev => ({ ...prev, schema: reorderSchemaBySections(newSchema, prev.sections) }));
    };

    const handleChange = (e, field) => {
        const { name, value, type, checked } = e.target;
        const updatedValues = {
            ...form.values,
            [name]: type === "checkbox" ? checked : value,
        };
        setForm(prev => ({ ...prev, values: updatedValues }));
        //onChange(updatedValues);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log(form.schema);
        console.log(form.values);
        onSubmit(form.values);
    };

    // Update handleNewFieldChange to support subtitle (for non-Editor fields)
    const handleNewFieldChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewField((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    // Add handler for CKEditor subtitle in newField
    const handleNewFieldSubtitleChange = (value) => {
        setNewField((prev) => ({
            ...prev,
            subtitle: value,
        }));
    };
    const handleEditFieldSubtitleChange = (value) => {
        setEditField((prev) => ({
            ...prev,
            subtitle: value,
        }));
    };
    const addField = (sectionName = "") => {
        if (!newField.name || !newField.label) return;
        const newSchema = [...form.schema, { ...newField, section: sectionName || newField.section }];
        setForm(prev => ({ ...prev, schema: reorderSchemaBySections(newSchema, prev.sections) }));
        setNewField({ name: "", label: "", type: "text", required: false, section: "" });
        setShowAddFieldModal(false);
        setAddFieldSection("");
    };

    // Section handlers
    const addSection = () => {
        if (!newSection) return;
        // If this is the first section and there are existing fields, move them to this section
        if (form.sections.length === 0 && form.schema.length > 0) {
            const updatedSchema = form.schema.map(f => ({ ...f, section: newSection }));
            setForm(prev => ({ ...prev, schema: updatedSchema }));
        }
        setForm(prev => ({
            ...prev,
            sections: [...prev.sections, newSection],
            sectionsubtitles: [...prev.sectionsubtitles, ""]
        }));
        setNewSection("");
    };

    // Section editing handlers
    const startEditSection = (idx) => {
        setEditingSectionIdx(idx);
        setEditingSectionValue(form.sections[idx]);
        setEditingSectionSubtitle(form.sectionsubtitles[idx] || "");
    };

    const saveEditSection = (idx) => {
        const oldSection = form.sections[idx];
        const newSections = [...form.sections];
        newSections[idx] = editingSectionValue;

        // Update all fields referencing this section
        const updatedSchema = form.schema.map(f =>
            f.section === oldSection ? { ...f, section: editingSectionValue } : f
        );

        // Update subtitle
        const newSubtitles = [...form.sectionsubtitles];
        newSubtitles[idx] = editingSectionSubtitle === "<p><br></p>" ? "" : editingSectionSubtitle;

        setForm(prev => ({
            ...prev,
            sections: newSections,
            schema: updatedSchema,
            sectionsubtitles: newSubtitles
        }));

        setEditingSectionIdx(null);
        setEditingSectionValue("");
        setEditingSectionSubtitle("");
    };

    const cancelEditSection = () => {
        setEditingSectionIdx(null);
        setEditingSectionValue("");
        setEditingSectionSubtitle("");
    };

    // Add Field Modal handlers
    const openAddFieldModal = (sectionName) => {
        setAddFieldSection(sectionName);
        setNewField({
            name: "",
            label: "",
            type: "text",
            required: false,
            section: sectionName,
        });
        setShowAddFieldModal(true);
    };

    // Edit field handlers
    const openEditModal = (index) => {
        setEditIndex(index);
        const field = { ...form.schema[index] };
        if (field.type === "select" && !field.options) {
            field.options = [];
        }
        setEditField(field);
        setShowEditModal(true);
        setNewOption({ value: "", label: "" });
    };

    const handleEditFieldChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditField((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    // Option editing handlers
    const handleOptionChange = (idx, key, val) => {
        setEditField((prev) => {
            const options = prev.options ? [...prev.options] : [];
            options[idx] = { ...options[idx], [key]: val };
            return { ...prev, options };
        });
    };

    const addOption = () => {
        if (!newOption.value || !newOption.label) return;
        setEditField((prev) => ({
            ...prev,
            options: prev.options ? [...prev.options, { ...newOption }] : [{ ...newOption }],
        }));
        setNewOption({ value: "", label: "" });
    };

    const deleteOption = (idx) => {
        setEditField((prev) => ({
            ...prev,
            options: prev.options.filter((_, i) => i !== idx),
        }));
    };

    const saveEditField = () => {
        const updatedSchema = [...form.schema];
        updatedSchema[editIndex] = { ...editField };
        setForm(prev => ({ ...prev, schema: updatedSchema }));
        setShowEditModal(false);
        setEditIndex(null);
        setEditField(null);
        setSelectedIdx(null);
    };

    const deleteField = (index) => {
        const updatedSchema = form.schema.filter((_, i) => i !== index);
        setForm(prev => ({ ...prev, schema: updatedSchema }));
        setSelectedIdx(null);
    };

    // dnd-kit sensors
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    // Section drop target with dnd-kit droppable
    function SectionDropTarget({ section, children, onDrop, isOver }) {
        const { setNodeRef, isOver: isDropping } = useDroppable({
            id: `section-${section || "none"}`,
            data: { section },
        });

        return (
            <div
                ref={setNodeRef}
                data-section={section}
                style={{
                    padding: "8px 0",
                    background: isDropping ? "#e0f7fa" : "#f8f9fa",
                    borderBottom: "1px solid #e9ecef",
                    transition: "background 0.2s",
                }}
            >
                <h6 className="mb-2">{section || "No Section"}</h6>
                {children}
            </div>
        );
    }

    // Drag and drop handlers for dnd-kit
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!active || !over) return;

        const activeIdx = form.schema.findIndex(f => f.name === active.id);

        // If dropped over a section drop target, move field to that section
        if (over.id && over.id.toString().startsWith("section-")) {
            const newSection = over.data?.current?.section || "";
            const updatedSchema = [...form.schema];
            updatedSchema[activeIdx] = { ...updatedSchema[activeIdx], section: newSection };
            setForm(prev => ({ ...prev, schema: updatedSchema }));
            return;
        }

        // Otherwise, reorder fields within the same section
        const overIdx = form.schema.findIndex(f => f.name === over.id);
        if (activeIdx !== overIdx) {
            setForm(prev => ({ ...prev, schema: arrayMove(prev.schema, activeIdx, overIdx) }));
        }
    };

    // Prepare all fields for global sorting
    const allFieldNames = form.schema.map(f => f.name);

    // Group fields by section
    const fieldsBySection = form.sections.length
        ? form.sections.map(section => ({
            section,
            fields: form.schema.filter(f => f.section === section),
        }))
        : [{ section: "", fields: form.schema }];

    // Inline edit form for selected question
    const renderInlineEdit = (field, idx) => (
        <Form className="mt-2 mb-2">
            <Form.Group className="mb-2">
                <Form.Label>Name (used to identify when making pdf)</Form.Label>
                <Form.Control
                    name="name"
                    value={editField.name}
                    onChange={handleEditFieldChange}
                />
            </Form.Group>
            <Form.Group className="mb-2">
                <Form.Label>Label</Form.Label>
                <Form.Control
                    name="label"
                    value={editField.label}
                    onChange={handleEditFieldChange}
                />
            </Form.Group>
            <Form.Group className="mb-2">
                <Form.Label>Subtitle (optional, HTML allowed)</Form.Label>
                <div style={{ backgroundColor:"#ffffff", minHeight: 80, padding: 4 }}>
                <CKEditor
                    editor={EditorBuild}
                    data={editField.subtitle || ""}
                    onChange={(event, editor) => handleEditFieldSubtitleChange(editor.getData())}
                />
                </div>
            </Form.Group>
            <Form.Group className="mb-2">
                <Form.Label>Type</Form.Label>
                <Form.Select
                    name="type"
                    value={editField.type}
                    onChange={handleEditFieldChange}
                >
                    <option value="text">Text</option>
                    <option value="number">Number</option>
                    <option value="select">Select</option>
                    <option value="checkbox">Checkbox</option>
                    <option value="radio">Radio</option>
                    <option value="textarea">Multiline Text</option>
                    <option value="date">Date</option>
                    <option value="email">Email</option> {/* <-- Add this line */}
                </Form.Select>
            </Form.Group>
            <Form.Group className="mb-2">
                <InputGroup>
                    <Form.Label>Required&nbsp;</Form.Label>
                    <Form.Check
                        type="switch"
                        name="required"
                        checked={editField.required}
                        onChange={handleEditFieldChange}
                    />
                </InputGroup>
            </Form.Group>
            <Form.Group className="mb-2">
                <Form.Label>Section</Form.Label>
                <Form.Select
                    name="section"
                    value={editField.section}
                    onChange={handleEditFieldChange}
                >
                    <option value="">No Section</option>
                    {form.sections.map((s, i) => (
                        <option key={i} value={s}>{s}</option>
                    ))}
                </Form.Select>
            </Form.Group>

            {(editField.type === "select" || editField.type === "radio") && (
                <div>
                    <Form.Label>Options</Form.Label>
                    {editField.options?.map((opt, idx) => (
                        <InputGroup className="mb-2" key={idx}>
                            <Form.Control
                                placeholder="Value"
                                value={opt.value}
                                onChange={e =>
                                    handleOptionChange(idx, "value", e.target.value)
                                }
                            />
                            <Form.Control
                                placeholder="Label"
                                value={opt.label}
                                onChange={e =>
                                    handleOptionChange(idx, "label", e.target.value)
                                }
                            />
                            <Button
                                variant="outline-danger"
                                onClick={() => deleteOption(idx)}
                            >
                                Delete
                            </Button>
                        </InputGroup>
                    ))}
                    <InputGroup className="mb-2">
                        <Form.Control
                            placeholder="Value"
                            value={newOption.value}
                            onChange={e =>
                                setNewOption({ ...newOption, value: e.target.value })
                            }
                        />
                        <Form.Control
                            placeholder="Label"
                            value={newOption.label}
                            onChange={e =>
                                setNewOption({ ...newOption, label: e.target.value })
                            }
                        />
                        <Button variant="success" onClick={addOption}>
                            Add
                        </Button>
                    </InputGroup>
                </div>
            )}
            <div className="d-flex gap-2">
                <Button variant="primary" size="sm" onClick={saveEditField}>
                    Save
                </Button>
                <Button variant="secondary" size="sm" onClick={(e) => setSelectedIdx(null)}>
                    Cancel
                </Button>
            </div>
        </Form>
    );

    // --- Combined editor for heading and subtitle ---
    const renderHeaderEdit = (
        <div className="mb-2">
            <Form.Control
                type="text"
                value={form.heading}
                onChange={e => setForm(prev => ({ ...prev, heading: e.target.value }))}
                autoFocus
                style={{ fontSize: "1.7em", fontWeight: "bold" }}
                placeholder="Enter form title"
                className="mb-2"
            />
            <div style={{ minHeight: 40 }}>
            <CKEditor
                editor={EditorBuild}
                data={form.subtitle || ""}
                onChange={(event, editor) => handleFormSubtitleChange(editor.getData())}
            />
            </div>
            <Button
                size="sm"
                variant="primary"
                className="mt-1"
                onClick={() => setEditingFormHeader(false)}
            >
                Save
            </Button>
        </div>
    );

    return (
        <div>
            <Row>
                <Col>
                    <Form>
                        <Row>
                            <Col>
                                <Form.Control
                                    placeholder="Section Name"
                                    value={newSection}
                                    onChange={e => setNewSection(e.target.value)}
                                />
                            </Col>
                            <Col>
                                <Button variant="success" onClick={addSection}>
                                    Add Section
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                </Col>
                <Col>
                    <Button variant="success" onClick={addDefaultField} disabled={form.sections.length > 0 }>
                        Add Field
                    </Button>
                </Col>
            </Row>
            {/* --- Editable Form Heading and Subtitle --- */}
            <div className="mb-3">
                {editingFormHeader ? (
                    renderHeaderEdit
                ) : (
                    <div onClick={() => setEditingFormHeader(true)} style={{ cursor: "pointer" }}>
                        <h3 className="form-heading" style={{ marginBottom: 0 }}>
                            {form.heading || <span style={{ color: "#888" }}>Click to add form title</span>}
                        </h3>
                        <div
                            className="form-subtitle text-muted"
                            style={{ fontSize: "1.1em" }}
                            dangerouslySetInnerHTML={{ __html: form.subtitle || "<span style='color:#bbb'>Click to add form subtitle</span>" }}
                        />
                    </div>
                )}
            </div>
            {/* --- End Editable Form Heading and Subtitle --- */}

            <Form onSubmit={handleSubmit}>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                    disabled={selectedIdx !== null}
                >
                    {fieldsBySection.map(({ section, fields }, sectionIdx) => (
                        <div key={sectionIdx}>
                            {section && (
                                <div className="mb-2">
                                    {editingSectionIdx === sectionIdx ? (
                                        <div>
                                            <Form.Control
                                                value={editingSectionValue}
                                                onChange={e => setEditingSectionValue(e.target.value)}
                                                size="sm"
                                                style={{ maxWidth: "250px", fontSize: "1.3em", fontWeight: "bold" ,display: "inline-block" }}
                                                className="me-2"
                                                autoFocus
                                            />
                                            <div style={{ maxWidth: 400, marginTop: 8}}>
                                                <CKEditor
                                                    editor={EditorBuild}
                                                    data={editingSectionSubtitle || ""}
                                                    onChange={(event, editor) => setEditingSectionSubtitle(editor.getData())}
                                                />
                                            </div>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                className="ms-2"
                                                onClick={() => saveEditSection(sectionIdx)}
                                            >
                                                Save
                                            </Button>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                className="ms-1"
                                                onClick={cancelEditSection}
                                            >
                                                Cancel
                                            </Button>
                                        </div>
                                    ) : (
                                        <div>
                                            <h4
                                                    className="mb-0"
                                                style={{ cursor: "pointer", fontSize: "1.3em", fontWeight: "bold",display: "inline-block" }}
                                                onClick={() => startEditSection(sectionIdx)}
                                            >
                                                {section}
                                            </h4>
                                            <Button
                                                variant="success"
                                                size="sm"
                                                className="ms-2"
                                                onClick={() => addDefaultField(section)}
                                            >
                                                Add Field
                                            </Button>
                                            {/* Section subtitle display */}
                                            {form.sectionsubtitles[sectionIdx] && (
                                                <div
                                                    className="text-muted"
                                                    style={{ fontSize: "0.98em", cursor: "pointer" }}
                                                    onClick={() => startEditSection(sectionIdx)}
                                                    dangerouslySetInnerHTML={{ __html: form.sectionsubtitles[sectionIdx] }}
                                                />
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            <SortableContext
                                items={fields.map(f => f.name)}
                                strategy={verticalListSortingStrategy}
                            >
                                <Stack direction="vertical" gap={3} className="mb-3">
                                    {fields.map((field, idx) => {
                                        const globalIdx = form.schema.findIndex(f => f === field);
                                        const isSelected = selectedIdx === globalIdx;
                                        return (
                                            <SortableField
                                                key={field.name}
                                                id={field.name}
                                                disableDrag={isSelected}
                                            >
                                                <div
                                                    className={`question-stack-item${isSelected ? " selected" : ""}`}
                                                    style={{ minWidth: "300px", cursor: "pointer" }}
                                                    onClick={() => {
                                                        if (!isSelected) {
                                                            setSelectedIdx(globalIdx);
                                                            setEditIndex(globalIdx);
                                                            setEditField({ ...field, options: field.options || [] });
                                                            setNewOption({ value: "", label: "" });
                                                        }
                                                    }}
                                                >
                                                    <Form.Group className="mb-3">
                                                        <Row>
                                                            <Col xs="auto" className="d-flex align-items-top">
                                                                <span className="me-2">{globalIdx + 1}.</span>
                                                            </Col>
                                                            <Col>
                                                                <Form.Label
                                                                    style={{ cursor: "pointer" }}
                                                                >
                                                                    {field.label}
                                                                    {field.required && <span style={{ color: "red" }}> *</span>}
                                                                </Form.Label>
                                                                {/* Render subtitle if present */}
                                                                {field.subtitle && (
                                                                    <div
                                                                        className="text-muted"
                                                                        style={{ fontSize: "0.95em" }}
                                                                        dangerouslySetInnerHTML={{ __html: field.subtitle }}
                                                                    />
                                                                )}
                                                                {field.type === "select" ? (
                                                                    <Form.Select
                                                                        name={field.name}
                                                                        value={form.values[field.name] || ""}
                                                                        onChange={(e) => handleChange(e, field)}
                                                                        required={field.required}
                                                                    >
                                                                        <option value="">Select...</option>
                                                                        {field.options?.map((opt) => (
                                                                            <option key={opt.value} value={opt.value}>
                                                                                {opt.label}
                                                                            </option>
                                                                        ))}
                                                                    </Form.Select>
                                                                ) : field.type === "checkbox" ? (
                                                                    <Form.Check
                                                                        type="checkbox"
                                                                        name={field.name}
                                                                        label={field.label}
                                                                        checked={!!form.values[field.name]}
                                                                        onChange={(e) => handleChange(e, field)}
                                                                        required={field.required}
                                                                    />
                                                                ) : field.type === "radio" ? (
                                                                    <div>
                                                                        {field.options?.map((opt, idx) => (
                                                                            <React.Fragment key={idx}>
                                                                                <Form.Check
                                                                                    type="radio"
                                                                                    name={field.name}
                                                                                    label={opt.label}
                                                                                    value={opt.value}
                                                                                    checked={form.values[field.name] === opt.value}
                                                                                    onChange={(e) => handleChange(e, field)}
                                                                                    required={field.required}
                                                                                    inline
                                                                                />
                                                                                <br />
                                                                            </React.Fragment>
                                                                        ))}
                                                                    </div>
                                                                ) : field.type === "textarea" ? (
                                                                    <Form.Control
                                                                        as="textarea"
                                                                        rows={3}
                                                                        name={field.name}
                                                                        value={form.values[field.name] || ""}
                                                                        onChange={(e) => handleChange(e, field)}
                                                                        required={field.required}
                                                                    />
                                                                ) : field.type === "date" ? (
                                                                    <Form.Control
                                                                        type="date"
                                                                        name={field.name}
                                                                        value={form.values[field.name] || ""}
                                                                        onChange={(e) => handleChange(e, field)}
                                                                        required={field.required}
                                                                    />
                                                                ) : field.type === "email" ? ( // <-- Add this block
                                                                    <Form.Control
                                                                        type="email"
                                                                        name={field.name}
                                                                        value={form.values[field.name] || ""}
                                                                        onChange={(e) => handleChange(e, field)}
                                                                        required={field.required}
                                                                    />
                                                                ) : (
                                                                    <Form.Control
                                                                        type={field.type}
                                                                        name={field.name}
                                                                        value={form.values[field.name] || ""}
                                                                        onChange={(e) => handleChange(e, field)}
                                                                        required={field.required}
                                                                    />
                                                                )}
                                                            </Col>
                                                            <Col xs="auto" className="d-flex align-items-center">
                                                                <Button
                                                                    variant="outline-danger"
                                                                    size="sm"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        deleteField(globalIdx);
                                                                    }}
                                                                >
                                                                    Delete
                                                                </Button>
                                                            </Col>
                                                            <Col xs="auto" className="d-flex align-items-center">
                                                                <Button
                                                                    variant="outline-success"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        setSelectedIdx(globalIdx);
                                                                        setEditIndex(globalIdx);
                                                                        setEditField({ ...field, options: field.options || [] });
                                                                        setNewOption({ value: "", label: "" });
                                                                    }}
                                                                >
                                                                    Edit
                                                                </Button>
                                                            </Col>
                                                        </Row>
                                                        {isSelected && editField && renderInlineEdit(field, globalIdx)}
                                                    </Form.Group>
                                                </div>
                                            </SortableField>
                                        );
                                    })}
                                </Stack>
                            </SortableContext>
                        </div>
                    ))}

                </DndContext>
                {/*<Button type="submit" variant="primary">*/}
                {/*    Submit*/}
                {/*</Button>*/}
            </Form>


            <hr />

        </div>
    );
}